/**
 * Hook pour les KPI de la homepage client.
 * Consomme l'endpoint léger /home-statistics dédié (quelques centaines d'octets)
 * au lieu de charger l'intégralité des données client via /missions (~850 KB).
 *
 * Les KPI sont pré-calculés côté admin (POST /projects/{id}/calculate-home-statistics).
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { HOME_KPI_QUERY_CONFIG } from '@/lib/queryConfig';

export interface HomeClientKpis {
  netBenefit: string;
  equivalentFTE: string;
  globalROI: string;
  amortization: string;
  projectDurationYears: number;
}

export interface UseHomeClientKpisResult {
  kpis: HomeClientKpis | null;
  loading: boolean;
  error: any;
  roiEnabled: boolean;
}

interface HomeStatisticsResponse {
  netBenefit?: string | null;
  equivalentFTE?: string | null;
  globalROI?: string | null;
  amortization?: string | null;
  projectDurationYears?: number;
  roiEnabled?: boolean;
  // Legacy
  economiesSur3Ans?: string;
  roiMoyen?: string;
  tempsAmortissement?: string;
}

export const homeKpiKeys = {
  byClient: (clientId: string) => ['homeClientKpis', clientId] as const,
};

export function useHomeClientKpis(clientId: string | undefined): UseHomeClientKpisResult {
  const { data, isLoading, error } = useQuery({
    queryKey: homeKpiKeys.byClient(clientId || ''),
    queryFn: () => apiFetch(`/v1/clients/${clientId}/home-statistics`) as Promise<HomeStatisticsResponse>,
    enabled: Boolean(clientId),
    ...HOME_KPI_QUERY_CONFIG,
  });

  const roiEnabled = data?.roiEnabled !== false;

  // Nouveau format (netBenefit, equivalentFTE, globalROI, amortization)
  const hasNewFormat = !!(data?.netBenefit && data?.globalROI);

  // Fallback legacy (economiesSur3Ans, roiMoyen, tempsAmortissement)
  const hasLegacyFormat = !!(data?.economiesSur3Ans && data?.roiMoyen);

  let kpis: HomeClientKpis | null = null;

  if (hasNewFormat) {
    kpis = {
      netBenefit: data!.netBenefit!,
      equivalentFTE: data!.equivalentFTE || '0',
      globalROI: data!.globalROI!,
      amortization: data!.amortization || '-',
      projectDurationYears: data!.projectDurationYears || 3,
    };
  } else if (hasLegacyFormat) {
    kpis = {
      netBenefit: data!.economiesSur3Ans!,
      equivalentFTE: '-',
      globalROI: data!.roiMoyen!,
      amortization: data!.tempsAmortissement || '-',
      projectDurationYears: data!.projectDurationYears || 3,
    };
  }

  return {
    kpis,
    loading: isLoading,
    error,
    roiEnabled,
  };
}
